/* eslint-disable no-undef, no-unused-vars */
import { makeAutoObservable } from "mobx";
import ApiStore from "./ApiStore";
import { RcFile } from "antd/es/upload";
import {
  AccessEmployee,
  CommunicationType,
  CommunicationTypeHelpArea,
  CommunityForProperty,
  DeliveryMethod,
  EmailDefault,
  GetCommunityData,
  JobItem,
  ManagmentCompany,
  PrintColorMethod,
  PrintColorType,
  PrintPreference,
  Property,
  ScopeDescription,
  SendOption,
} from "../../api/Api_v1";
import moment from "moment";
import { PropertyJobDetail, RecipientInfo } from "../../api/Api_v2";
import { Tuple } from "../../api/types";

export type MailJob = {
  files: MailJobFile[];
  employeeKey: number;
  deliveryMethodId?: number;
  printColorId?: number;
  printPreferenceMethodId?: number;
  communicationTypeId: number;
};

export type MailJobFile = {
  name: string;
  guid: string;
  file: RcFile;
  filePages: number;
  fileInfo: CamFileInfo;
};

export type CamFileInfo = {
  inputFileBase64?: string;
  communityKey?: number;
  account?: number;
  communitiesKeys?: number[];
  blocks?: string[];
  inspectionGroup?: string[];
  recipient?: RecipientInfo;
};

export type Job = {
  JobID: number;
  CommunicationType: string;
  CommunicationTypeId: number;
  Property: string;
  ProcessedBy: string;
  DateCreated: string;
  Recipients: string;
  DateApproved: string;
  Status: string;
  StatusId?: number;
  EmployeeKey: number;
  isReviewed: boolean;
  isCanApprove: boolean;
};

export type CommunitiesData = {
  communitiesId: number;
  accounts: Tuple<string, string>[];
  address: Tuple<string, string>[];
  names: Tuple<string, string>[];
};

class ApplicationStore {
  private _api: ApiStore;
  isLoading: boolean = true;
  isAuth: boolean = false;
  accessEmployee: AccessEmployee = {} as AccessEmployee;
  communicationTypes: CommunicationType[] = [];
  communicationType: CommunicationType = {} as CommunicationType;
  communities: Property[] = [];
  communitiesData: CommunitiesData[] = [];

  isOpenJobList: boolean = false;
  isJobListItemID: string = "";

  mailJob: MailJob = {} as MailJob;
  isUploading: boolean = false;
  isLoadCommunitiesData: boolean = false;
  selectedFiles: string[] = [];
  printColorTypeList: PrintColorType[] = [];
  managmentCompany: ManagmentCompany[] = [];

  isAccessProperty: boolean = false;

  communityForPropertyList: CommunityForProperty[] = [];
  communityBlockList: Tuple<string, string>[] = [];
  communityGroupeList: Tuple<string, string>[] = [];

  communicationTypeHelpArea: CommunicationTypeHelpArea =
    {} as CommunicationTypeHelpArea;
  selectedDeliveryMethod: DeliveryMethod[] = [];
  selectedPrintColorMethod: PrintColorMethod[] = [];
  selectedPrintPreferenceMethod: PrintPreference[] = [];
  selectedSendOptionMethod: SendOption[] = [];
  scopeDescriptionList: ScopeDescription[] = [];
  defaultValuesForMessagePage: EmailDefault = {} as EmailDefault;

  statuses = [
    { statusId: 1, statusName: "Sent to Nordis" },
    { statusId: 2, statusName: "Waiting for Approval" },
    { statusId: 3, statusName: "Processing" },
    { statusId: 4, statusName: "Rejected" },
    { statusId: 5, statusName: "Cancelled" },
  ];

  constructor(api: ApiStore) {
    makeAutoObservable(this);
    this._api = api;
  }

  onInit(): void {
    this._api.Api_v1.getAccessEmployee()
      .then((data) => {
        if (data) {
          this.accessEmployee = data;
          this.mailJob.employeeKey = parseInt(data.employeeKey);
          const promises = [];
          promises.push(
            this._api.Api_v1.getCommunicationTypeList().then((data) => {
              if (data) {
                this.communicationTypes = data;
              }
            })
          );
          promises.push(
            this._api.Api_v1.getPropertyListAsync().then((data) => {
              if (data) {
                this.communities = data;
              }
            })
          );
          promises.push(
            this._api.Api_v1.getPrintColorTypeList().then((data) => {
              if (data) {
                this.printColorTypeList = data;
              }
            })
          );
          promises.push(
            this._api.Api_v1.getManagmentCompany().then((data) => {
              if (data) {
                this.managmentCompany = data;
              }
            })
          );

          Promise.all(promises)
            .then(() => {
              this.isLoading = false;
            })
            .catch(() => {
              this.isLoading = false;
            });

          this.isAuth = true;
        }
      })
      .catch(() => {
        this.isLoading = false;
      });
  }

  setIsOpenData(job: string): void {
    if (!this.isOpenJobList && this.isJobListItemID === "") {
      this.isOpenJobList = true;
      this.isJobListItemID = job;
    }
  }

  setIsOpenJobList(isOpen: boolean): void {
    this.isOpenJobList = isOpen;
  }

  getIdFromProperty(fileName: string): number {
    return parseInt(fileName.split(".")[0]);
  }

  getAccNumberFromAccNumber(fileName: string): string {
    return fileName.split(".")[1];
  }

  checkFileName(fileName: string): boolean {
    return /\d{5}\.\d{6}|\d{7}\.\D{3}/.test(fileName);
  }

  setIsLoadCommunitiesData(isLoading: boolean): void {
    this.isLoadCommunitiesData = isLoading;
  }

  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  setSelectedFiles(selected: string[]): void {
    this.selectedFiles = selected;
  }

  setCommunicationType(communicationType: CommunicationType): void {
    this.communicationType = communicationType;
    this.mailJob = {
      ...this.mailJob,
      communicationTypeId: communicationType.comTypeId,
    };

    this._api.Api_v1.getCommunicationTypeHelpArea(
      communicationType.comTypeId
    ).then((res) => {
      this.communicationTypeHelpArea = res;
    });

    this._api.Api_v1.getSelectedDeliveryMethod(
      communicationType.comTypeId
    ).then((res) => {
      this.selectedDeliveryMethod = res;
      if (res.length === 0) return;
      let defaultItem = res.find((i) => i.dm_Active === 1);
      if (!defaultItem) defaultItem = res[0];
      this.setDeliveryMethod(defaultItem!.id);
    });

    this._api.Api_v1.getSelectedPrintColorMethod(
      communicationType.comTypeId
    ).then((res) => {
      this.selectedPrintColorMethod = res;
      if (res.length === 0) return;
      let defaultItem = res.find((i) => i.pc_Active === 1);
      if (!defaultItem) defaultItem = res[0];
      this.setColorMode(defaultItem!.id);
    });

    this._api.Api_v1.getSelectedPrintPreferenceMethod(
      communicationType.comTypeId
    ).then((res) => {
      this.selectedPrintPreferenceMethod = res;
      if (res.length === 0) return;
      let defaultItem = res.find((i) => i.pp_Active === 1);
      if (!defaultItem) defaultItem = res[0];
      this.setPrintPreferences(defaultItem!.id);
    });

    this._api.Api_v1.getScopeDescriptionList(
      communicationType.comTypeId
    ).then((res) => {
      this.scopeDescriptionList = res;
    });

    this._api.Api_v1.getDefaultValuesForMessagePage(
      communicationType.comTypeId
    ).then((res) => {
      this.defaultValuesForMessagePage = res;
    });
  }

  getSelectedHelpText(deliveryMethodName: string): Promise<string> {
    return this._api.Api_v1.getSelectedHelpText(deliveryMethodName);
  }

  setSelectedFileData(fileGuid: string, data: Record<any, any>): void {
    this.mailJob.files = this.mailJob.files?.map((_) => {
      if (_.guid === fileGuid) {
        return { ..._, fileInfo: { ..._.fileInfo, ...data } };
      }
      return _;
    });
  }

  private setCommunitiesData(
    communityKey: number,
    accounts: Tuple<string, string>[],
    address: Tuple<string, string>[],
    names: Tuple<string, string>[]
  ): void {
    if (this.communitiesData.length > 0) {
      const isExist = this.communitiesData.find(
        (_) => _.communitiesId === communityKey
      );
      if (isExist) {
        isExist.accounts = accounts;
        isExist.address = address;
        isExist.names = names;
      } else {
        this.communitiesData = [
          ...this.communitiesData,
          {
            communitiesId: communityKey,
            accounts,
            address,
            names,
          } as CommunitiesData,
        ];
      }
    } else {
      this.communitiesData = [
        {
          communitiesId: communityKey,
          accounts,
          address,
          names,
        } as CommunitiesData,
      ];
    }
  }

  getGeneralFormData(communityKey: number): void {
    if (
      this.mailJob.files?.every((_) => _.fileInfo.communityKey === communityKey)
    ) {
      this.setIsLoadCommunitiesData(true);
      const getCommunityListPromise = this._api.Api_v1.getCommunityListForProperty(
        communityKey
      );
      const getBlockPromise = this._api.Api_v1.getBlocks({
        propertyId: communityKey,
        communicationId: this.mailJob.communicationTypeId.toString(),
        communityId: [],
      });
      const getGroupsPromise = this._api.Api_v1.getGroups({
        propertyId: communityKey,
        communicationId: this.mailJob.communicationTypeId.toString(),
        communityId: [],
      });
      Promise.all([
        getCommunityListPromise,
        getBlockPromise,
        getGroupsPromise,
      ]).then((res): void => {
        this.setIsLoadCommunitiesData(false);
        this.communityForPropertyList = res[0] as CommunityForProperty[];
        this.communityBlockList = res[1] as Tuple<string, string>[];
        this.communityGroupeList = res[2] as Tuple<string, string>[];
      });
    }
  }

  clearGeneralFormData(): void {
    if (this.communityForPropertyList.length > 0) {
      this.communityForPropertyList = [];
      this.communityBlockList = [];
      this.communityGroupeList = [];
    }
  }

  getCommunitiesData(
    communityKey: number,
    account?: number,
    guid?: string
  ): void {
    if (!this.communitiesData.find((_) => _.communitiesId === communityKey)) {
      this.setIsLoadCommunitiesData(true);
      this.setCommunitiesData(communityKey, [], [], []);
      if (guid) this.setSelectedFileData(guid, { account: undefined });
      const property = this.communities.find((_) => _.id === communityKey);
      if (property) {
        this._api.Api_v1.isAccessToProperty(property.name).then((isAccess) => {
          if (isAccess) {
            const fileData = this.mailJob.files?.find((_) => _.guid === guid);
            if (fileData) {
              if (fileData.fileInfo.communityKey !== communityKey) {
                this.setSelectedFileData(fileData.guid, {
                  communityKey: communityKey,
                });
              }
            }
            this._api.Api_v1.getCommunityListForProperty(communityKey).then(
              (communityListForPropertyRes) => {
                const dataSearch: GetCommunityData = {
                  propertyId: communityKey,
                  communicationId: this.mailJob.communicationTypeId.toString(),
                  communityId: communityListForPropertyRes.map(
                    (_) => _.communityKey
                  ),
                };
                Promise.all([
                  this._api.Api_v1.getAccounts(dataSearch),
                  this._api.Api_v1.getAddress(dataSearch),
                  this._api.Api_v1.getNames(dataSearch),
                ]).then((res): void => {
                  this.setIsLoadCommunitiesData(false);
                  this.setCommunitiesData(communityKey, res[0], res[1], res[2]);
                  setTimeout(() => {
                    if (res[0].some((i) => i.item2 == String(account))) {
                      if (account && guid) {
                        this.setSelectedFileData(guid, {
                          account: String(account),
                        });
                      }
                    }
                  }, 100);
                });
              }
            );
          } else {
            this.isAccessProperty = true;
            setTimeout(() => {
              this.isAccessProperty = false;
              this.setIsLoadCommunitiesData(false);
            }, 500);
          }
        });
      }
    } else {
      this.setIsLoadCommunitiesData(true);
      const property = this.communities.find((_) => _.id === communityKey);
      if (property) {
        this._api.Api_v1.isAccessToProperty(property.name).then((isAccess) => {
          if (isAccess) {
            const fileData = this.mailJob.files?.find((_) => _.guid === guid);
            if (fileData && fileData.fileInfo.communityKey !== communityKey) {
              this.setSelectedFileData(fileData.guid, {
                communityKey: communityKey,
              });
            }
            this.setIsLoadCommunitiesData(false);
          } else {
            this.isAccessProperty = true;
            setTimeout(() => {
              this.isAccessProperty = false;
              this.setIsLoadCommunitiesData(false);
            }, 500);
          }
        });
      }
    }
  }

  uploadFile(file: RcFile): void {
    this.isUploading = true;
    const formData = new FormData();
    formData.append("uploads", file);

    const getFileBase64 = (file: RcFile): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]);
        };
        reader.onerror = (error) => reject(error);
      });

    const placeholderFile: MailJobFile = {
      name: file.name,
      guid: `placeholder-${file.name}-${Date.now()}`,
      file,
      filePages: 0,
      fileInfo: {} as CamFileInfo,
    };

    this.mailJob = {
      ...this.mailJob,
      files: this.mailJob.files
        ? [...this.mailJob.files, placeholderFile]
        : [placeholderFile],
    };

    this._api.Api_v2.UploadAndReviewFilesInfo(formData).then((res) => {
      if (res.length > 0 && res[0].isUpload) {
        const newFile: MailJobFile = {
          name: res[0].fileName,
          guid: res[0].fileGuid,
          file,
          filePages: res[0].pages,
          fileInfo: {} as CamFileInfo,
        };

        if (this.communicationType.comTypeId === 1) {
          const communityKey = this.getIdFromProperty(res[0].fileName);
          const filterCommunities = this.communities
            .map((_) => _.id)
            .find((_) => _ === communityKey);
          getFileBase64(file).then((base64): void => {
            newFile.fileInfo.inputFileBase64 = base64;
            this.setSelectedFileData(newFile.guid, {
              inputFileBase64: base64,
            });
          });
          if (filterCommunities) {
            const communitiesName = this.communities.find(
              (_) => _.id === communityKey
            );
            if (communitiesName) {
              this.setIsLoadCommunitiesData(true);
              this._api.Api_v1.isAccessToProperty(communitiesName.name).then(
                (isAccess) => {
                  if (isAccess) {
                    this.setSelectedFileData(newFile.guid, {
                      communityKey: filterCommunities,
                    });
                    this.setIsLoadCommunitiesData(false);
                  } else {
                    this.isAccessProperty = true;
                    setTimeout(() => {
                      this.isAccessProperty = false;
                      this.setIsLoadCommunitiesData(false);
                    }, 500);
                  }
                }
              );
            }
          }
        } else {
          if (this.checkFileName(res[0].fileName)) {
            const communityKey = this.getIdFromProperty(res[0].fileName);
            const filterCommunities = this.communities
              .map((_) => _.id)
              .find((_) => _ === communityKey);
            if (filterCommunities) {
              newFile.fileInfo.communityKey = filterCommunities;
              const communityData = this.communitiesData.find(
                (_) => _.communitiesId === filterCommunities
              );
              const account = this.getAccNumberFromAccNumber(res[0].fileName);
              if (communityData) {
                const findAccount = communityData.accounts.find((_) =>
                  account.includes(_.item2)
                )?.item2;
                if (findAccount) {
                  newFile.fileInfo.account = parseInt(findAccount);
                }
              } else {
                this.getCommunitiesData(
                  filterCommunities,
                  parseInt(account),
                  newFile.guid
                );
              }
            }
          } else if (
            this.getIdFromProperty(res[0].fileName).toString().length === 5 ||
            this.getIdFromProperty(res[0].fileName).toString().length === 6
          ) {
            const communityKey = this.getIdFromProperty(res[0].fileName);
            const filterCommunities = this.communities
              .map((_) => _.id)
              .find((_) => _ === communityKey);
            if (filterCommunities) {
              this.getCommunitiesData(
                filterCommunities,
                undefined,
                newFile.guid
              );
            }
          }
        }

        this.mailJob = {
          ...this.mailJob,
          files: this.mailJob.files.map((f) =>
            f.guid === placeholderFile.guid ? { ...newFile, fileInfo: { ...newFile.fileInfo, ...f.fileInfo } } : f
          ),
        };
      } else {
        this.mailJob = {
          ...this.mailJob,
          files: this.mailJob.files.filter(
            (f) => f.guid !== placeholderFile.guid
          ),
        };
      }

      this.isUploading = false;
    });
  }

  deleteFiles(): void {
    if (this.selectedFiles.length > 0) {
      const newFiles: MailJobFile[] = [];
      this.mailJob.files?.forEach((_) => {
        if (!this.selectedFiles.includes(_.guid)) {
          newFiles.push(_);
        }
      });
      this.mailJob.files = newFiles;
      this.selectedFiles = [];
    }
  }

  async getRecipientAsync(
    fileInfo: MailJobFile,
    comTypeId: number
  ): Promise<void> {
    if (fileInfo && comTypeId > 0) {
      this._api.Api_v2.GetRecipientInfo(fileInfo.fileInfo, comTypeId).then(
        (res) => {
          if (res) {
            this.setSelectedFileData(fileInfo.guid, { recipient: res });
          }
        }
      );
    }
  }

  async getJobListAsync(statusId?: number, startDate?: string, endDate?: string): Promise<Job[]> {
    const dateValidation = (date: string): string => {
      if (date === null) return "";
      return new Date(date).getFullYear() > 1900
        ? moment(date).format("MM/DD/YYYY")
        : "";
    };
    return await this._api.Api_v1.getGetJobList(
      this.accessEmployee.employeeKey,
      this.accessEmployee.isCanApprove,
      statusId,
      startDate,
      endDate
    ).then((res) => {
      return res.map((i: JobItem): Job => {
        let recipients = "Problem";
        if (i.mailingJobRecipients.length > 1) {
          recipients = "Multiple";
        } else if (
          i.mailingJobRecipients.length > 0 &&
          i.mailingJobRecipients[0] !== null
        ) {
          recipients =
            i.mailingJobRecipients[0].firstName +
            " " +
            i.mailingJobRecipients[0].lastName;
        }
        const status = this.statuses.find(
          (s) => s.statusId === i.statusId
        )?.statusName;
        const communicationType = this.communicationTypes.find(
          (t) => t.comTypeId === i.comTypeId
        )?.comTypeName;
        let isCanApprove = false;
        if (i.statusId === 2) {
          isCanApprove = i.jobApprovals.some(
            (j) =>
              (String(j.approverEmployeeKey) ===
                this.accessEmployee.employeeKey ||
                this.accessEmployee.isCanApprove) &&
              !j.isApproved &&
              String(j.submitterEmployeeKey) !== this.accessEmployee.employeeKey
          );
        }
        return {
          JobID: i.mailingJobId,
          CommunicationType: communicationType || "Empty",
          CommunicationTypeId: i.comTypeId,
          Property: i.propertyName || "Empty",
          ProcessedBy: i.employeeName || "Empty",
          EmployeeKey: i.employeeKey,
          DateCreated: dateValidation(i.dateCreated),
          Recipients: recipients,
          DateApproved: i.dateApproved
            ? dateValidation(i.dateApproved)
            : "—",
          Status: status || "Empty",
          StatusId: i.statusId,
          isReviewed: false,
          isCanApprove,
        };
      });
    });
  }

  async GetDocumentPreviewFile(mailJobID: number) {
    return await this._api.Api_v1.getDocumentPreviewFile(mailJobID);
  }

  async GetMailJobRecipientExelFile(mailJobID: number) {
    return await this._api.Api_v1.getMailJobRecipientExelFile(mailJobID);
  }

  setDeliveryMethod(id: number): void {
    this.mailJob = { ...this.mailJob, deliveryMethodId: id };
  }

  setPrintPreferences(id: number): void {
    this.mailJob = { ...this.mailJob, printPreferenceMethodId: id };
  }

  setColorMode(id: number): void {
    this.mailJob = { ...this.mailJob, printColorId: id };
  }

  async getExelFile(): Promise<any> {
    return await this._api.Api_v2.GetExelFile(this.mailJob);
  }

  async GetJobDescription(): Promise<PropertyJobDetail[]> {
    const res = await this._api.Api_v2.GetJobDescription(this.mailJob);
    return (res || []).map((i) => {
      const files = this.mailJob.files?.filter(
        (f) => (f.fileInfo.communityKey as any) == i.communityKey
      );
      return {
        ...i,
        propertyName: this.communities.find(
          (c) => (c.id as any) == i.communityKey
        )!.titleInDropdown!,
        files: files || [],
      };
    });
  }

  async Create(): Promise<number> {
    return await this._api.Api_v2.Create(this.mailJob);
  }

  async Cancel(): Promise<number> {
    return await this._api.Api_v2.Cancel(this.mailJob);
  }

  cleanMailJob(): void {
    this.mailJob.files = [];
    this.selectedFiles = [];
    this.communitiesData = [];
    this.communityForPropertyList = [];
    this.communityBlockList = [];
    this.communityGroupeList = [];
    this.setCommunicationType(this.communicationType);
  }

  async Approve(mailJobId: number): Promise<boolean> {
    return await this._api.Api_v2.Approve(
      this.accessEmployee.employeeKey,
      mailJobId
    );
  }

  async Reject(mailJobId: number, reason: string): Promise<boolean> {
    return await this._api.Api_v2.Reject(
      this.accessEmployee.employeeKey,
      mailJobId,
      reason
    );
  }
  reorderFiles(fromIndex: number, toIndex: number): void {
    const files = [...this.mailJob.files];
    const [moved] = files.splice(fromIndex, 1);
    files.splice(toIndex, 0, moved);
    this.mailJob = { ...this.mailJob, files };
}
}
export default ApplicationStore;